import React from 'react';
import { motion } from 'framer-motion';
import { Clock3, ExternalLink, MapPin, Phone } from 'lucide-react';
import { useRestaurant } from '../../context/RestaurantContext';
import ArabAliLogo from '../../components/ArabAliLogo';

const ContactPage: React.FC = () => {
  const { restaurant, settings, phoneNumber, callHref, mapsHref } = useRestaurant();

  const contactInfo = [
    {
      icon: <Phone className="h-6 w-6" />,
      title: 'Phone',
      value: phoneNumber,
      href: callHref ?? null,
      color: 'bg-amber-300/15 text-amber-300',
    },
    {
      icon: <MapPin className="h-6 w-6" />,
      title: 'Location',
      value: restaurant.location,
      href: mapsHref,
      color: 'bg-[#be372c]/15 text-[#f58b68]',
    },
    {
      icon: <Clock3 className="h-6 w-6" />,
      title: 'Opening Hours',
      value: 'Daily: 7:00 AM - 11:00 PM',
      href: null,
      color: 'bg-white/10 text-white',
    },
  ];

  return (
    <div className="px-4 pb-24 pt-4">
      <div className="mx-auto max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="arab-panel mb-6 p-6 text-center sm:p-8"
        >
          <ArabAliLogo
            className="justify-center"
            logoImage={settings.logoImage}
            name={restaurant.name}
            subtitle={settings.subtitle || 'Restaurant'}
            location={restaurant.location}
          />
          <h1 className="mt-6 font-heading text-4xl font-semibold text-white">Contact {restaurant.name}</h1>
          <p className="mt-3 text-white/60">Call the restaurant, open the location in Maps, or go back to the live menu.</p>
        </motion.div>

        <div className="mb-8 grid gap-4 md:grid-cols-3">
          {contactInfo.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              {item.href ? (
                <a href={item.href} className="arab-panel group block p-5 transition-all duration-300 hover:-translate-y-1">
                  <div className="flex items-center gap-4">
                    <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${item.color}`}>{item.icon}</div>
                    <div className="flex-1">
                      <h3 className="font-bold text-white">{item.title}</h3>
                      <p className="mt-1 text-white/80">{item.value}</p>
                    </div>
                    <ExternalLink className="h-5 w-5 text-white/30 transition-colors group-hover:text-amber-300" />
                  </div>
                </a>
              ) : (
                <div className="arab-panel block p-5">
                  <div className="flex items-center gap-4">
                    <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${item.color}`}>{item.icon}</div>
                    <div>
                      <h3 className="font-bold text-white">{item.title}</h3>
                      <p className="mt-1 text-white/80">{item.value}</p>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="arab-panel mt-8 overflow-hidden"
        >
          <div className="grid gap-0 md:grid-cols-[0.95fr_1.05fr]">
            <div className="relative min-h-[280px] overflow-hidden">
              <img src={restaurant.coverImage} alt={restaurant.name} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d0403] via-transparent to-transparent" />
            </div>
            <div className="p-6 sm:p-8">
              <div className="text-[11px] uppercase tracking-[0.35em] text-amber-100/50">Find us</div>
              <h2 className="mt-3 font-heading text-3xl font-semibold text-white">{restaurant.name}</h2>
              <p className="mt-4 text-base leading-7 text-white/68">
                Use the actions below to call the restaurant or open the location in Google Maps.
              </p>
              <div className="mt-6 rounded-3xl border border-white/10 bg-white/5 p-5">
                <div className="text-sm uppercase tracking-[0.28em] text-white/45">Current details</div>
                <div className="mt-3 space-y-2 text-white/78">
                  <a
                    href={mapsHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-amber-200 transition hover:text-amber-100"
                  >
                    <MapPin className="h-4 w-4" />
                    <span>{restaurant.location}</span>
                    <ExternalLink className="h-4 w-4" />
                  </a>
                  <div>{phoneNumber}</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ContactPage;
